const rotulosTabela: Record<string, string> = {
  atuacao: 'atuação',
  comarca: 'comarca',
  configuracoes: 'configuração',
  conselho_superior: 'conselho superior',
  curso: 'curso',
  defensor: 'defensor',
  defensoria: 'defensoria',
  distrito: 'distrito',
  diretoria: 'diretoria',
  endereco: 'endereço',
  estagiario: 'estagiário',
  estagiario_anexo: 'anexo de estagiário',
  estagio: 'estágio',
  feriado: 'feriado',
  funcao: 'função',
  funcao_salario: 'salário da função',
  historico: 'histórico',
  instituicao_ensino: 'instituição de ensino',
  lotacao: 'lotação',
  movimentacao: 'atividade',
  movimentacao_registro: 'registro de atividade',
  movimentacaoregistro: 'registro de atividade',
  movimentacao_tipo: 'tipo de movimentação',
  movimentacaotipo: 'tipo de movimentação',
  notificacao: 'notificação',
  nucleo: 'núcleo',
  periodo: 'ação',
  pessoa: 'pessoa',
  tipo_gasto: 'tipo de gasto',
  gasto: 'gasto',
  predio: 'prédio',
  regra: 'regra',
  responsavel: 'responsável',
  setor: 'setor',
  substituicao: 'substituição',
  telefone: 'telefone',
  usuario: 'usuário',
  vara: 'vara',
};

const camposOcultos = new Set([
  'senha',
  'password',
  'codigorecuperacao',
  'criadoem',
  'editadoem',
  'criadopor',
  'editadopor',
  'id',
]);

export function rotuloAcao(acao: string): string {
  if (acao === 'INSERT') return 'Inclusão';
  if (acao === 'UPDATE') return 'Atualização';
  if (acao === 'DELETE') return 'Exclusão';
  return acao;
}

export function rotuloTabela(tabela: string): string {
  const nome = rotulosTabela[tabela] ?? tabela.replaceAll('_', ' ');
  return nome.charAt(0).toUpperCase() + nome.slice(1);
}

export function rotuloAcaoTabela(acao: string, tabela: string): string {
  return `${rotuloAcao(acao)} ${rotuloTabela(tabela).toLowerCase()}`;
}

export function chavesDiffVisiveis(diff: Record<string, unknown> | null | undefined): string[] {
  if (!diff) return [];
  return Object.keys(diff).filter((campo) => !campoOculto(campo));
}

function campoOculto(campo: string): boolean {
  const normalizado = campo.toLowerCase();
  return camposOcultos.has(normalizado) || normalizado.endsWith('_hash');
}

function campoSenha(campo: string): boolean {
  const normalizado = campo.toLowerCase();
  return normalizado === 'senha' || normalizado === 'password';
}

function campoSecreto(campo: string): boolean {
  const normalizado = campo.toLowerCase();
  return normalizado === 'codigorecuperacao' || normalizado.endsWith('_hash');
}

function valorGravado(valor: unknown): unknown {
  if (valor == null || typeof valor !== 'object' || Array.isArray(valor)) return valor ?? null;
  const chaves = Object.keys(valor);
  if (chaves.length === 0 || chaves.some((chave) => chave !== 'antes' && chave !== 'depois')) return valor;
  return (valor as { depois?: unknown }).depois ?? null;
}

function ehParAntesDepois(valor: unknown): valor is { antes?: unknown; depois?: unknown } {
  if (valor == null || typeof valor !== 'object' || Array.isArray(valor)) return false;
  const chaves = Object.keys(valor);
  return chaves.length > 0 && chaves.every((chave) => chave === 'antes' || chave === 'depois');
}

function textoValor(valor: unknown): string {
  if (valor === undefined) return '—';
  if (valor === null) return 'null';
  if (typeof valor === 'string') return valor;
  if (typeof valor === 'number' || typeof valor === 'boolean') return String(valor);
  return JSON.stringify(valor, null, 2);
}

export function jsonDetalheHistorico(diff: Record<string, unknown> | null | undefined): string | null {
  if (!diff) return null;

  const objeto: Record<string, unknown> = {};
  for (const [campo, valor] of Object.entries(diff)) {
    if (campoSenha(campo)) {
      objeto.senha = 'alterada';
      continue;
    }
    if (campoSecreto(campo)) {
      objeto[campo] = 'alterado';
      continue;
    }
    objeto[campo] = valorGravado(valor);
  }

  if (Object.keys(objeto).length === 0) return null;
  return JSON.stringify(objeto, null, 2);
}

export type LinhaDiffHistorico = {
  campo: string;
  removido?: string;
  adicionado?: string;
};

function valorEfetivoDoDiff(valor: unknown): unknown {
  return ehParAntesDepois(valor) ? valor.depois : valor;
}

/** Aplica um evento sobre o estado acumulado (logs do mais antigo ao mais novo). */
export function aplicarDiffAoEstado(
  estado: Record<string, unknown>,
  diff: Record<string, unknown> | null | undefined,
  acao?: string,
): Record<string, unknown> {
  if (!diff) return estado;
  if (acao === 'DELETE') return {};

  const proximo = acao === 'INSERT' ? {} : { ...estado };
  for (const [campo, valor] of Object.entries(diff)) {
    if (campoOculto(campo) && !campoSenha(campo)) continue;
    proximo[campo] = valorEfetivoDoDiff(valor);
  }
  return proximo;
}

/**
 * logs em ordem DESC (mais recente primeiro).
 * Retorna o estado do registro imediatamente antes do evento em `indice`.
 */
export function estadoAntesDoEvento(
  logs: Array<{ diff?: unknown; acao?: string }>,
  indice: number,
): Record<string, unknown> {
  let estado: Record<string, unknown> = {};
  for (let i = logs.length - 1; i > indice; i -= 1) {
    const log = logs[i];
    estado = aplicarDiffAoEstado(
      estado,
      log.diff as Record<string, unknown> | null | undefined,
      log.acao,
    );
  }
  return estado;
}

export function linhasDiffHistorico(
  diff: Record<string, unknown> | null | undefined,
  acao?: string,
  estadoAnterior?: Record<string, unknown>,
): LinhaDiffHistorico[] {
  if (!diff) return [];

  const linhas: LinhaDiffHistorico[] = [];
  for (const [campo, valor] of Object.entries(diff)) {
    if (campoOculto(campo) && !campoSenha(campo)) continue;

    if (campoSenha(campo) || campoSecreto(campo)) {
      if (acao === 'DELETE') {
        linhas.push({ campo, removido: '••••' });
      } else if (acao === 'INSERT') {
        linhas.push({ campo, adicionado: '••••' });
      } else {
        linhas.push({ campo, removido: '••••', adicionado: '••••' });
      }
      continue;
    }

    if (ehParAntesDepois(valor)) {
      const removido = Object.prototype.hasOwnProperty.call(valor, 'antes')
        ? textoValor(valor.antes)
        : undefined;
      const adicionado = Object.prototype.hasOwnProperty.call(valor, 'depois')
        ? textoValor(valor.depois)
        : undefined;
      if (removido != null && adicionado != null && removido === adicionado) continue;
      linhas.push({ campo, removido, adicionado });
      continue;
    }

    if (acao === 'DELETE') {
      linhas.push({ campo, removido: textoValor(valor) });
      continue;
    }

    if (acao === 'UPDATE' && estadoAnterior && Object.prototype.hasOwnProperty.call(estadoAnterior, campo)) {
      linhas.push({
        campo,
        removido: textoValor(estadoAnterior[campo]),
        adicionado: textoValor(valor),
      });
      continue;
    }

    linhas.push({ campo, adicionado: textoValor(valor) });
  }

  return linhas;
}
